/**
 * Putting a practice video into storage from the browser (D-668, D-669, D-670).
 *
 * <b>Three steps, because on the host that matters the bytes never touch the
 * app.</b> She asks for permission, the browser writes straight to storage, she
 * comes back to claim it. A 900 MB routine through the App Service is 900 MB of
 * a Basic worker that is also serving the register at 4:30 on a class night.
 *
 * <b>Which of the two write paths runs is the server's answer, never a guess
 * here.</b> A null `uploadUrl` means this host cannot issue a permission — local
 * disk, on a developer's machine — and the bytes go through the app instead
 * (D-669). Both end at the same claim.
 */

import { landed, transfer } from './mediaUpload'

/** What the server hands back when it reserves a destination. */
export type UploadTicket = {
  grantId: string
  /** Null where the host has no direct-to-storage path (D-669). */
  uploadUrl: string | null
  expiresAt: string
}

/** The row the claim made, ready to go into the editor's list. */
export type ClaimedVideo = {
  mediaAssetId: string
  fileName: string
  sizeBytes: number
}

/**
 * What a practice video may be, client-side.
 *
 * <b>The same list the server holds, deliberately duplicated.</b> The server is
 * the authority and refuses on its own; this copy exists so a wrong file is
 * refused on the phone before a permission is issued and before a byte moves —
 * the spec asks for the reason *before* any permission to write (scenario 7).
 * If the two ever disagree the server wins and the teacher sees its sentence.
 */
const ALLOWED_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v']

/** What the file picker offers. Kept beside the list it mirrors. */
export const VIDEO_ACCEPT = `${ALLOWED_TYPES.join(',')},.mp4,.mov,.webm,.m4v`

/**
 * Why this file cannot be uploaded, or null.
 *
 * <b>Type and emptiness only — deliberately not size.</b> The ceiling is
 * `Media:MaxVideoBytes`, which is configuration the browser has no copy of, and
 * a hardcoded number here would drift the day somebody raises it — producing a
 * refusal on a file the server would have taken, which is the gate-that-fires-
 * on-a-correct-thing this project keeps warning about. The reserve refuses an
 * oversized file in one request carrying no bytes, which is already *before any
 * permission to write is issued*, so nothing is lost by leaving it there.
 *
 * <b>A phone often reports no type at all</b> for a file from the camera roll,
 * so an empty `type` is allowed through rather than refused — refusing on a
 * blank string would block the ordinary case on iOS.
 */
export function whyNotAVideo(file: File): string | null {
  if (file.type && !ALLOWED_TYPES.includes(file.type)) {
    return 'Practice videos can be MP4, MOV or WebM.'
  }

  if (file.size === 0) return 'That file is empty.'

  return null
}

/** Whatever sentence the server wrote, whichever shape it used. */
async function reasonFrom(res: Response, fallback: string): Promise<string> {
  const problem = await res.json().catch(() => null)

  // The three doors of this feature answer in two shapes: `Results.Problem`
  // gives detail/title, `Results.BadRequest` gives error. Reading all three
  // means a refusal is never rendered as a status code.
  return problem?.detail ?? problem?.title ?? problem?.error ?? `${fallback} (${res.status})`
}

/*
 * `send` was defined here, and its own comment called it "the first in this
 * codebase" - which stopped being true the moment the messaging upload grew a
 * second copy. It is shared from `./upload` now (A-17, D-901); nothing about
 * what this file does with it has changed.
 */

/**
 * Reserve, write, claim.
 *
 * <b>`onProgress` covers the write only</b>, which is the part that takes
 * minutes; the two requests either side are milliseconds and moving the bar for
 * them would make it stall at 99% while the claim runs.
 */
export async function uploadChoreoVideo(
  classId: string,
  file: File,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<ClaimedVideo> {
  const base = `/api/classes/${classId}/choreo/uploads`

  const reserved = await fetch(base, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fileName: file.name,
      // A phone often sends nothing; the server's own list is what decides,
      // and mp4 is what a camera roll actually holds.
      contentType: file.type || 'video/mp4',
      sizeBytes: file.size,
    }),
    signal,
  })

  if (!reserved.ok) throw new Error(await reasonFrom(reserved, 'Could not start the upload'))

  const ticket: UploadTicket = await reserved.json()

  /*
   * <b>The write is the shared core's</b> (D-909). What stays here is this
   * door's wording and its own reading of a status — which differs from the
   * messaging door's on purpose, and is the reason the core returns what
   * happened rather than judging it.
   */
  const written = await transfer(
    file,
    {
      uploadUrl: ticket.uploadUrl,
      throughApp: `${base}/${ticket.grantId}/content`,
      /*
       * The same fallback the reserve above sends. A phone often reports no
       * type at all for a camera-roll file (`whyNotAVideo` lets that through
       * on purpose), and the two must agree.
       */
      contentType: file.type || 'video/mp4',
    },
    {
      /*
       * **No step name here, deliberately.** This door has only ever shown the
       * transport's own sentence, and naming a step it does not have produced
       * *"The upload stopped before it finished.: The upload stopped before it
       * finished."* on the live cellular path. Giving it a real step name is a
       * change worth making on its own, with its own test — not smuggled into
       * a refactor whose ledger row says *behaviour unchanged*.
       */
    },
    { onProgress, signal },
  )

  if (written.via === 'storage') {
    /*
     * <b>403 here is ambiguous and must not be retried.</b> The permission is
     * create-only, so a second write to a blob that already exists is refused
     * exactly as an expired one is — and the realistic cause is a first attempt
     * that succeeded while the phone changed network and lost the answer.
     * Claiming is what settles it: the claim asks storage what is actually
     * there and refuses a file that never landed.
     */
    /*
     * ⚠ **Not `landed()`, and not by oversight.** That helper accepts any 2xx
     * and refuses everything else — which here would accept a **200** where
     * only a 201 means the bytes are there.
     *
     * **Which 403s are ambiguous is the core's judgement, not this door's**
     * (A-17, D-906). It keyed on the raw status until 2026-10-19, which was
     * right while every write was one request and wrong the moment blocks
     * arrived: a refused *block* is a spent permission and nothing downstream
     * can rescue it, while a refused *commit* most likely means the bytes
     * already landed. The core tells those apart; reading the number here
     * would be the second implementation of that rule.
     */
    if (written.status !== 201 && !written.settleWithTheFinishingStep) {
      throw new Error(
        written.status === 0
          ? 'The upload stopped before it finished.'
          : `The video could not be stored (${written.status}).`,
      )
    }
  } else if (!landed(written)) {
    const problem = (() => {
      try {
        return JSON.parse(written.text)
      } catch {
        return null
      }
    })()

    throw new Error(
      problem?.detail ?? problem?.title ?? problem?.error ?? 'The video could not be stored.',
    )
  }

  const claimed = await fetch(`${base}/${ticket.grantId}/claim`, { method: 'POST', signal })

  if (!claimed.ok) throw new Error(await reasonFrom(claimed, 'The upload did not finish'))

  return claimed.json()
}
